import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators,} from '@angular/forms';
import {CountriesService} from '../service/countries.service';
import {Subscription} from 'rxjs';
import {LanguageService} from '../service/language.service';
import {Router} from '@angular/router';
import {ShoppingSharedService} from "../service/shopping-shared.service";
import {SAVED_ADDRESS} from "../constants/key.constants";
import {REMEMBER_ADDRESS} from "../constants/global.constants";
import {ShippingType} from "../enums/ShippingType";
import {ShippingUtility} from "../utilityClass/shippingUtility";

@Component({
  selector: 'app-shipping-address',
  templateUrl: './shipping-address.component.html',
  styleUrls: ['./shipping-address.component.scss', "./shipping-address-media.component.scss"]
})
export class ShippingAddressComponent implements OnInit, OnDestroy {
  protected contactForm: FormGroup;
  private languageSubscription: Subscription | undefined;
  countries!: string[];
  products: any[] = [];
  totalSumAmd!: number;
  totalSumUsd!: number;
  selectedDeliveryMethod: ShippingType = ShippingType.STANDARD;

  constructor(
    private formBuilder: FormBuilder,
    private countriesService: CountriesService,
    private languageService: LanguageService,
    private router: Router,
    private shoppingSheredService: ShoppingSharedService) {
    this.contactForm = this.buildContactForm();
    const savedShippingType = this.shoppingSheredService.shippingType;
    this.shoppingSheredService.clearSessionData();
    if (savedShippingType) {
      this.shoppingSheredService.shippingType = savedShippingType;
    }
    this.selectedDeliveryMethod = savedShippingType || ShippingType.STANDARD;

    const navData = this.router.getCurrentNavigation()?.extras.state;
    if (navData) {
      this.products = navData['shopData'] || [];
      this.totalSumAmd = navData['totalSumAmd'];
      this.totalSumUsd = navData['totalSumUsd'];
      this.shoppingSheredService.products = this.products;
      this.shoppingSheredService.totalSumAmd = this.totalSumAmd;
      this.shoppingSheredService.totalSumUsd = this.totalSumUsd;
    }
  }

  ngOnInit(): void {
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(
      (lang: string) => {
        this.fetchCountries(lang);
        this.setDefaultCountry(lang);
        const savedFormData = this.shoppingSheredService.formData;
        const savedProducts = this.shoppingSheredService.products;
        this.totalSumAmd = this.shoppingSheredService.totalSumAmd;
        this.totalSumUsd = this.shoppingSheredService.totalSumUsd;
        this.selectedDeliveryMethod = this.shoppingSheredService.shippingType || ShippingType.STANDARD;

        if (savedFormData) {
          this.contactForm.patchValue(savedFormData);
        }

        if (savedProducts.length) {
          this.products = savedProducts;
        }
      }
    );
    this.autofillAddress();
  }

  private setDefaultCountry(language: string): void {
    const defaultCountry = {
      hy: 'Հայաստան',
      en: 'Armenia',
      ru: 'Армения',
    }[language];

    const countryControl = this.contactForm.get('country');
    if (countryControl?.value !== defaultCountry) {
      countryControl?.setValue(defaultCountry);
    }
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  private fetchCountries(language: string): void {
    this.countriesService.getCountries(language).subscribe({
      next: (res) => (this.countries = res),
      error: (err) => console.error('Error fetching countries:', err),
    });
  }

  private buildContactForm(): FormGroup {
    return this.formBuilder.group({
      country: ['', [Validators.required]],
      address: ['', [Validators.required, Validators.maxLength(100)]],
      apartment: ['', [Validators.maxLength(50)]],
      postalCode: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]+$')]],
      city: ['', [Validators.required, Validators.maxLength(50)]],
      rememberAddress: [true],
    });
  }

  onSubmit() {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    const formData = this.contactForm.value;
    sessionStorage.setItem('formData', JSON.stringify(formData));
    sessionStorage.setItem('shopData', JSON.stringify(this.products));
    sessionStorage.setItem('totalSumAmd', this.totalSumAmd.toString());
    sessionStorage.setItem('totalSumUsd', this.totalSumUsd.toString());
    this.shoppingSheredService.formData = formData;
    this.shoppingSheredService.products = this.products;
    this.shoppingSheredService.totalSumAmd = this.totalSumAmd;
    this.shoppingSheredService.totalSumUsd = this.totalSumUsd;
    this.router.navigate(['product/payment']);
  }

  saveAddress() {
    if (this.contactForm.get(REMEMBER_ADDRESS)?.value) {
      const formData = this.contactForm.value;
      localStorage.setItem(SAVED_ADDRESS, JSON.stringify(formData));
    } else {
      localStorage.removeItem(SAVED_ADDRESS);
    }
  }

  autofillAddress() {
    const savedAddress = localStorage.getItem('savedAddress');
    if (savedAddress) {
      const formData = JSON.parse(savedAddress);
      this.contactForm.patchValue(formData);
    }
  }

  returnToShippingMethod() {
    this.router.navigate(['/shipping-method'], {
      state: {
        shopData: this.products,
        totalSumAmd: this.totalSumAmd,
        totalSumUsd: this.totalSumUsd,
      },
    });
  }

  getShippingMethodName(): string {
    return ShippingUtility.getShippingMethodName(this.selectedDeliveryMethod);
  }

  protected readonly ShippingType = ShippingType;
}
